import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Scan, X, Printer, ToggleLeft, ToggleRight, History, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SECTION_NAME_MAP = {
  'fob': 'FOB Section',
  'greige': 'Greige Section',
  'greige-storage': 'Greige Storage',
  'goods-transfer': 'Goods Transfer',
  'processing': 'Processing Section',
  'dispatch': 'Dispatch Section',
  'inhouse': 'Inhouse Section',
  'flexi': 'Flexi Section',
  'sample': 'Sample Section',
  'tissue': 'Tissue Section',
  'ecom': 'ECOM Section',
  'unassorted': 'Unassorted Section',
  'sales-return': 'Sales Return',
  'trims': 'Trims Section',
  'yarn': 'Yarn Section',
  'shirt-manufacturing': 'Shirt Manufacturing',
  'accounts': 'Accounts Section',
  'house-keeping': 'House Keeping',
  'electrical-goods': 'Electrical Goods',
  'it-goods': 'IT Goods',
  'r-and-m': 'R & M',
  'admin-section': 'Admin Section',
  'weaving': 'Weaving Section',
};

export default function MiscOutward() {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const sectionName = SECTION_NAME_MAP[sectionId] || sectionId;

  const [tab, setTab] = useState('create');
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [isOtherVendor, setIsOtherVendor] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [returnable, setReturnable] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [scannedItems, setScannedItems] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [printSlip, setPrintSlip] = useState(null);
  const scanInputRef = useRef(null);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab]);

  const fetchVendors = async () => {
    try {
      const res = await apiClient.get('/misc-outward/vendors');
      setVendors(res.data.vendors || []);
    } catch {
      setVendors([]);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await apiClient.get(`/misc-outward/history?section=${encodeURIComponent(sectionName)}`);
      setHistory(res.data.slips || []);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleVendorChange = (value) => {
    if (value === '__other__') {
      setIsOtherVendor(true);
      setSelectedVendor('');
    } else {
      setIsOtherVendor(false);
      setSelectedVendor(value);
      setContactName('');
      setContactNumber('');
    }
  };

  const handleScan = async () => {
    const code = scanInput.trim();
    if (!code) return;

    if (scannedItems.some(i => i.code === code)) {
      toast.error(`'${code}' already scanned`);
      setScanInput('');
      return;
    }

    setScanning(true);
    try {
      const res = await apiClient.get(`/misc-outward/validate-scan?code=${encodeURIComponent(code)}&section=${encodeURIComponent(sectionName)}`);
      const item = res.data;
      setScannedItems(prev => [...prev, item]);
      toast.success(`${item.type === 'bundle' ? 'Bundle' : 'USN'} '${code}' validated (${item.details?.usn_count || 1} USNs)`);
      setScanInput('');
      scanInputRef.current?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || `'${code}' not found in ${sectionName}`);
    } finally {
      setScanning(false);
    }
  };

  const handleRemoveItem = (code) => {
    setScannedItems(prev => prev.filter(i => i.code !== code));
  };

  const totalUsnCount = scannedItems.reduce((sum, i) => sum + (i.usn_codes?.length || i.details?.usn_count || 0), 0);

  const handleSubmit = async () => {
    if (!selectedVendor && !isOtherVendor) {
      toast.error('Please select a vendor');
      return;
    }
    if (isOtherVendor && (!contactName.trim() || !contactNumber.trim())) {
      toast.error('Contact name and number are required');
      return;
    }
    if (!remarks.trim()) {
      toast.error('Remarks are required');
      return;
    }
    if (scannedItems.length === 0) {
      toast.error('Scan at least one USN or bundle');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        section: sectionName,
        vendor_name: isOtherVendor ? contactName.trim() : selectedVendor,
        is_other_vendor: isOtherVendor,
        contact_name: isOtherVendor ? contactName.trim() : null,
        contact_number: isOtherVendor ? contactNumber.trim() : null,
        returnable,
        remarks: remarks.trim(),
        scanned_items: scannedItems,
      };
      const res = await apiClient.post('/misc-outward/submit', payload);
      toast.success(res.data.message);

      // Fetch and show print slip
      const slipRes = await apiClient.get(`/misc-outward/slip/${res.data.slip_number}`);
      setPrintSlip(slipRes.data);

      // Reset form
      setSelectedVendor('');
      setIsOtherVendor(false);
      setContactName('');
      setContactNumber('');
      setReturnable(false);
      setRemarks('');
      setScannedItems([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !printSlip) return;
    const itemsHtml = printSlip.items.map((item, idx) => `
      <tr>
        <td style="border:1px solid #ccc;padding:4px 8px;text-align:center">${idx + 1}</td>
        <td style="border:1px solid #ccc;padding:4px 8px;font-family:monospace">${item.code}</td>
        <td style="border:1px solid #ccc;padding:4px 8px">${item.type}</td>
        <td style="border:1px solid #ccc;padding:4px 8px">${item.details?.item_barcode || '-'}</td>
        <td style="border:1px solid #ccc;padding:4px 8px">${item.details?.design_name || '-'}</td>
        <td style="border:1px solid #ccc;padding:4px 8px;text-align:center">${item.usn_codes?.length || 0}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html><head><title>Misc Outward Slip - ${printSlip.slip_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 13px; }
        h2 { margin-bottom: 4px; }
        .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin: 12px 0; }
        .meta span { color: #666; }
        .meta strong { color: #111; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th { background: #f0f0f0; border: 1px solid #ccc; padding: 6px 8px; text-align: left; font-size: 12px; }
        .footer { margin-top: 40px; display: flex; justify-content: space-between; }
        .footer div { border-top: 1px solid #333; padding-top: 4px; width: 180px; text-align: center; font-size: 12px; }
        @media print { body { padding: 10px; } }
      </style></head><body>
      <h2>MISCELLANEOUS OUTWARD SLIP</h2>
      <p style="color:#666;margin-top:0">${printSlip.slip_number}</p>
      <hr/>
      <div class="meta">
        <div><span>Section:</span> <strong>${printSlip.section}</strong></div>
        <div><span>Date:</span> <strong>${new Date(printSlip.created_at).toLocaleDateString('en-IN')}</strong></div>
        <div><span>Vendor:</span> <strong>${printSlip.is_other_vendor ? printSlip.contact_name : printSlip.vendor_name}</strong></div>
        <div><span>Returnable:</span> <strong>${printSlip.returnable ? 'YES' : 'NO'}</strong></div>
        ${printSlip.is_other_vendor ? `<div><span>Contact:</span> <strong>${printSlip.contact_number}</strong></div>` : ''}
        <div><span>Total USNs:</span> <strong>${printSlip.total_usn_count}</strong></div>
        <div><span>Remarks:</span> <strong>${printSlip.remarks}</strong></div>
        <div><span>Created By:</span> <strong>${printSlip.created_by_name}</strong></div>
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Code</th><th>Type</th><th>Barcode</th><th>Design</th><th>USNs</th>
        </tr></thead>
        <tbody>${itemsHtml}</tbody>
      </table>
      <div class="footer">
        <div>Section In-Charge</div>
        <div>Gate Entry Staff</div>
        <div>Receiver</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/sections/${sectionId}`)} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <ExternalLink className="w-5 h-5 text-rose-400" />
          <div>
            <h1 className="text-lg font-bold">Misc Outward — {sectionName}</h1>
            <p className="text-xs text-[#E8DCC8]">Send goods outward for sample/misc purposes</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="create" data-testid="tab-create">New Outward</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="create">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Form */}
              <div className="space-y-5">
                {/* Vendor */}
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Vendor *</Label>
                  {!isOtherVendor ? (
                    <Select value={selectedVendor} onValueChange={handleVendorChange}>
                      <SelectTrigger data-testid="vendor-select"><SelectValue placeholder="Select vendor..." /></SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.vendor_name || v} value={v.vendor_name || v}>{v.vendor_name || v}</SelectItem>
                        ))}
                        <SelectItem value="__other__">Other (Manual Entry)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs bg-amber-50 border-amber-300 text-amber-700">Other Vendor</Badge>
                        <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => { setIsOtherVendor(false); setContactName(''); setContactNumber(''); }}>
                          Back to list
                        </Button>
                      </div>
                      <Input placeholder="Contact Name *" value={contactName} onChange={e => setContactName(e.target.value)} data-testid="contact-name" />
                      <Input placeholder="Contact Number *" value={contactNumber} onChange={e => setContactNumber(e.target.value)} data-testid="contact-number" />
                    </div>
                  )}
                </div>

                {/* Returnable Toggle */}
                <div className="flex items-center justify-between border rounded-lg p-3 bg-white">
                  <div>
                    <Label className="text-sm font-medium">Returnable</Label>
                    <p className="text-xs text-gray-500">Mark if goods are expected to be returned</p>
                  </div>
                  <Switch checked={returnable} onCheckedChange={setReturnable} data-testid="returnable-toggle" />
                </div>

                {/* Remarks */}
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Remarks *</Label>
                  <Textarea placeholder="Purpose of outward..." value={remarks} onChange={e => setRemarks(e.target.value)} className="min-h-[60px]" data-testid="remarks" />
                </div>

                {/* Scan */}
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Scan USN / Bundle</Label>
                  <div className="flex gap-2">
                    <Input
                      ref={scanInputRef}
                      placeholder="Scan or type USN/Bundle code..."
                      value={scanInput}
                      onChange={e => setScanInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleScan(); }}
                      disabled={scanning}
                      data-testid="scan-input"
                    />
                    <Button onClick={handleScan} disabled={scanning || !scanInput.trim()} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="scan-btn">
                      <Scan className="w-4 h-4 mr-1" />{scanning ? '...' : 'Scan'}
                    </Button>
                  </div>
                </div>

                {/* Submit */}
                <Button onClick={handleSubmit} disabled={submitting || scannedItems.length === 0} className="w-full h-11 bg-rose-600 hover:bg-rose-700 text-white" data-testid="submit-btn">
                  {submitting ? 'Submitting...' : `Submit Outward (${scannedItems.length} items, ${totalUsnCount} USNs)`}
                </Button>
              </div>

              {/* Right: Scanned Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#1A1F2E]">Scanned Items</h3>
                  <span className="text-xs text-gray-500">{scannedItems.length} items, {totalUsnCount} USNs</span>
                </div>
                {scannedItems.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-400 text-sm">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No items scanned yet
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {scannedItems.map((item, idx) => (
                      <div key={item.code} className="border rounded-lg p-3 bg-white flex items-center justify-between" data-testid={`scanned-item-${item.code}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{idx + 1}</span>
                            <span className="font-mono font-semibold text-sm text-[#1A1F2E]">{item.code}</span>
                            <Badge variant="outline" className={`text-[10px] ${item.type === 'bundle' ? 'bg-purple-50 text-purple-700 border-purple-300' : 'bg-blue-50 text-blue-700 border-blue-300'}`}>
                              {item.type}
                            </Badge>
                          </div>
                          <div className="flex gap-3 mt-1 text-xs text-gray-500">
                            {item.details?.item_barcode && <span>Barcode: {item.details.item_barcode}</span>}
                            {item.details?.design_name && <span>{item.details.design_name}</span>}
                            <span>{item.usn_codes?.length || item.details?.usn_count || 0} USNs</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleRemoveItem(item.code)} data-testid={`remove-${item.code}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            {loadingHistory ? (
              <p className="text-center text-gray-400 py-8">Loading...</p>
            ) : history.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No outward history for this section</p>
            ) : (
              <div className="space-y-3">
                {history.map(slip => (
                  <div key={slip.slip_number} className="border rounded-lg p-4 bg-white" data-testid={`history-${slip.slip_number}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-sm">{slip.slip_number}</span>
                        {slip.returnable && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">Returnable</Badge>}
                        <Badge variant="outline" className={`text-[10px] ${slip.status === 'OUT' ? 'bg-red-50 text-red-700 border-red-300' : 'bg-green-50 text-green-700 border-green-300'}`}>
                          {slip.status}
                        </Badge>
                      </div>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                        try {
                          const res = await apiClient.get(`/misc-outward/slip/${slip.slip_number}`);
                          setPrintSlip(res.data);
                        } catch { toast.error('Failed to load slip'); }
                      }} data-testid={`print-${slip.slip_number}`}>
                        <Printer className="w-3 h-3 mr-1" />Print
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs text-gray-600">
                      <div><span className="text-gray-400">Vendor:</span> {slip.is_other_vendor ? slip.contact_name : slip.vendor_name}</div>
                      <div><span className="text-gray-400">Items:</span> {slip.total_scan_count} ({slip.total_usn_count} USNs)</div>
                      <div><span className="text-gray-400">By:</span> {slip.created_by_name}</div>
                      <div><span className="text-gray-400">Date:</span> {new Date(slip.created_at).toLocaleDateString('en-IN')}</div>
                    </div>
                    {slip.remarks && <p className="text-xs text-gray-500 mt-1">Remarks: {slip.remarks}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Print Slip Dialog */}
      <Dialog open={printSlip !== null} onOpenChange={() => setPrintSlip(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="print-slip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Misc Outward Slip</span>
              <Button size="sm" onClick={handlePrint} data-testid="print-btn">
                <Printer className="w-4 h-4 mr-1" />Print A4
              </Button>
            </DialogTitle>
          </DialogHeader>
          {printSlip && (
            <div className="space-y-3 text-sm">
              <div className="font-mono font-bold text-lg">{printSlip.slip_number}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-400">Section:</span> {printSlip.section}</div>
                <div><span className="text-gray-400">Date:</span> {new Date(printSlip.created_at).toLocaleString('en-IN')}</div>
                <div><span className="text-gray-400">Vendor:</span> {printSlip.is_other_vendor ? `${printSlip.contact_name} (${printSlip.contact_number})` : printSlip.vendor_name}</div>
                <div><span className="text-gray-400">Returnable:</span> {printSlip.returnable ? 'Yes' : 'No'}</div>
                <div className="col-span-2"><span className="text-gray-400">Remarks:</span> {printSlip.remarks}</div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100"><tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Barcode</th>
                    <th className="p-2 text-center">USNs</th>
                  </tr></thead>
                  <tbody>
                    {printSlip.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{idx + 1}</td>
                        <td className="p-2 font-mono">{item.code}</td>
                        <td className="p-2">{item.type}</td>
                        <td className="p-2">{item.details?.item_barcode || '-'}</td>
                        <td className="p-2 text-center">{item.usn_codes?.length || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-gray-400 text-right">Total: {printSlip.total_usn_count} USNs in {printSlip.total_scan_count} items</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
